import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { isMobileBreakpoint, fetchConfig } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {HTMLButtonElement | null} */
  #clearCartButton = null;

  /** @returns {HTMLDialogElement | null} */
  get #clearCartConfirmDialog() {
    const drawerDialog = this.refs.dialog instanceof HTMLDialogElement ? this.refs.dialog : null;
    const confirmDialog = drawerDialog?.querySelector('.cart-drawer__confirm-dialog');
    return confirmDialog instanceof HTMLDialogElement ? confirmDialog : null;
  }

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /** @param {MouseEvent} event */
  handleClearDialogBackdropClick(event) {
    if (event.currentTarget instanceof HTMLDialogElement && event.target === event.currentTarget) {
      this.cancelClearCart();
    }
  }

  /** @param {Event} event */
  handleClearDialogCancel(event) {
    event.preventDefault();
    this.cancelClearCart();
  }

  #closeClearCartDialog() {
    const dialog = this.#clearCartConfirmDialog;
    if (dialog?.open) dialog.close();
  }

  #showClearCartDialog() {
    const dialog = this.#clearCartConfirmDialog;
    if (!dialog) return false;
    if (!dialog.open) dialog.showModal();
    return true;
  }

  /** @param {boolean} isLoading */
  #setClearCartLoading(isLoading) {
    const dialog = this.#clearCartConfirmDialog;
    if (!dialog) return;

    dialog.toggleAttribute('data-loading', isLoading);
    dialog.querySelectorAll('.cart-drawer__confirm-actions button').forEach((button) => {
      if (button instanceof HTMLButtonElement) {
        button.disabled = isLoading;
      }
    });
  }

  async #executeClearCart() {
    const button = this.#clearCartButton;
    if (button) button.disabled = true;

    try {
      const response = await fetch('/cart/clear.js', fetchConfig('json'));
      const cart = await response.json();
      this.#closeClearCartDialog();
      this.dispatchEvent(new CartUpdateEvent(cart, this.id, { itemCount: 0 }));
    } catch (error) {
      console.error(error);
    } finally {
      this.#setClearCartLoading(false);
      if (button) button.disabled = false;
      this.#clearCartButton = null;
    }
  }

  /**
   * Opens clear-cart confirmation before clearing the cart.
   * @param {Event} [event] - The triggering event.
   */
  clearCart(event) {
    const target = event?.currentTarget || event?.target;
    this.#clearCartButton = target instanceof HTMLButtonElement ? target : null;

    if (!this.#showClearCartDialog()) {
      console.warn('Clear cart confirmation dialog is missing.');
    }
  }

  cancelClearCart() {
    this.#setClearCartLoading(false);
    this.#closeClearCartDialog();
    this.#clearCartButton = null;
  }

  confirmClearCart() {
    this.#setClearCartLoading(true);
    this.#executeClearCart();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
